import { useCallback, useEffect, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import * as THREE from 'three'
import BoardMesh from './BoardMesh'
import CameraRig from './CameraRig'
import Highlights from './Highlights'
import MarblePiece from './MarblePiece'
import SceneEnvironment from './SceneEnvironment'
import { BOARD_THICKNESS, BOARD_TOP_Y, BOARD_RADIUS, SCENE_DIAMETER, positionKey, keepContextAlive } from './constants'
import { createBackdropTexture } from './textures'
import type { GameState, Move, Piece, Position } from '../../stores/gameStore'

interface GameSceneProps {
  pieces: Piece[]
  marbleStyle: GameState['marbleStyle']
  selectedPiece: Position | null
  validMoves: Move[]
  animatingPiece: { from: Position; to: Position; path?: Position[] } | null
  onPositionClick: (pos: Position) => void
}

const SHADOW_BOUND = BOARD_RADIUS * 1.06

function Backdrop() {
  const texture = useMemo(() => createBackdropTexture(), [])
  useEffect(() => () => texture.dispose(), [texture])
  return (
    <mesh position={[0, BOARD_TOP_Y - BOARD_THICKNESS - 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <planeGeometry args={[SCENE_DIAMETER * 2.4, SCENE_DIAMETER * 2.4]} />
      <meshStandardMaterial map={texture} roughness={0.96} metalness={0} envMapIntensity={0.2} />
    </mesh>
  )
}

export default function GameScene({
  pieces,
  marbleStyle,
  selectedPiece,
  validMoves,
  animatingPiece,
  onPositionClick,
}: GameSceneProps) {
  const selectedKey = selectedPiece ? positionKey(selectedPiece) : null
  const movingKey = animatingPiece ? positionKey(animatingPiece.from) : null

  const targets = useMemo(
    () => (animatingPiece ? [] : validMoves.map((move) => move.end)),
    [animatingPiece, validMoves],
  )

  const handlePick = useCallback(
    (q: number, r: number) => onPositionClick({ q, r }),
    [onPositionClick],
  )

  return (
    <Canvas
      orthographic
      camera={{ position: [0, 48, 0], near: 0.1, far: 120 }}
      shadows={{ type: THREE.PCFSoftShadowMap }}
      gl={{ antialias: true, preserveDrawingBuffer: true }}
      onCreated={({ gl }) => keepContextAlive(gl)}
    >
      <color attach="background" args={['#e2d0ac']} />
      <CameraRig />
      <SceneEnvironment />

      <ambientLight intensity={0.46} color="#fff1d6" />
      <hemisphereLight intensity={0.62} color="#fff0d0" groundColor="#5c3a20" />
      <directionalLight
        position={[-11, 18, 8]}
        intensity={1.75}
        color="#ffe6bc"
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-near={1}
        shadow-camera-far={50}
        shadow-camera-left={-SHADOW_BOUND}
        shadow-camera-right={SHADOW_BOUND}
        shadow-camera-top={SHADOW_BOUND}
        shadow-camera-bottom={-SHADOW_BOUND}
        shadow-bias={-0.0004}
        shadow-radius={4}
      />
      <directionalLight position={[10, 9, -9]} intensity={0.35} color="#bcd6ff" />

      <Backdrop />
      <BoardMesh onPositionClick={handlePick} />
      <Highlights selected={animatingPiece ? null : selectedPiece} targets={targets} />

      {pieces.map((piece) => {
        const key = positionKey(piece.position)
        const isMoving = key === movingKey && animatingPiece
        const path = isMoving
          ? animatingPiece.path && animatingPiece.path.length > 1
            ? animatingPiece.path
            : [animatingPiece.from, animatingPiece.to]
          : undefined

        return (
          <MarblePiece
            key={`piece-${piece.player}-${key}`}
            position={piece.position}
            player={piece.player}
            marbleStyle={marbleStyle}
            selected={!isMoving && key === selectedKey}
            movePath={path}
            onClick={() => handlePick(piece.position.q, piece.position.r)}
          />
        )
      })}
    </Canvas>
  )
}
